import type { NetworkConnection, NetworkNode } from '@amarantos/api-client'
import { createContext, useContext } from 'react'

/**
 * Lets a custom graph node trigger "add upstream manhole" for its own node. Provided by
 * `NetworkGraph` and consumed by `ManholeNode`, kept in its own module so the two don't import each
 * other (circular) — and so the callback never has to be threaded through React Flow's node `data`,
 * which would churn the layout memo. `undefined` in read-only (status) mode: no affordance.
 */
const AddUpstreamContext = createContext<((nodeId: NetworkNode['id']) => void) | undefined>(undefined)

export const AddUpstreamProvider = AddUpstreamContext.Provider
export const useAddUpstream = () => useContext(AddUpstreamContext)

/**
 * Lets a pipe's label select its own pipe. React Flow's `onEdgeClick` only fires for the edge's path in
 * the SVG, and `EdgeLabelRenderer` portals the label out to a layer of its own — so a label that answers
 * the pointer has to reach the same callback the pane's handler does, and this is the one channel that
 * gets it there. Travels alongside `AddUpstreamContext` for the same reasons: no import cycle, and
 * nothing threaded through edge `data` that would churn the layout memo.
 */
const SelectConnectionContext = createContext<((connectionId: NetworkConnection['id']) => void) | undefined>(undefined)

export const SelectConnectionProvider = SelectConnectionContext.Provider
export const useSelectConnection = () => useContext(SelectConnectionContext)
